import { computed, watch } from 'vue';
import { filter, tap, delay } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { useAccountStore } from 'src/stores/account';
import { useCentralStore } from 'src/stores/central';
import { useThreadingStore } from 'src/stores/threading';
import cloneDeep from 'lodash.clonedeep';
/**
 * Plugin for basic user related methods
 * i.e watch user log in log out and if user is online
 * and for retrying calls to the backend
 */
const retryWatchPlugin = () => {
    const accountStore = useAccountStore();
    const centralStore = useCentralStore();
    const threadingStore = useThreadingStore();
    const user = computed(() => accountStore.user);
    const { subject: userWatcher$ } = rxInitSubjectCallback();
    /**
     * Retry calls to the backend
     * threading type calls only
     */
    function retryCalls() {
        const idToRemove = [];
        for (let i = 0; i < centralStore.retryAxios.length; i++) {
            const axiosData = centralStore.retryAxios[i];
            // skip for direct type calls as its
            // hard for it to be retried
            if (axiosData.type === 'direct') {
                idToRemove.push(axiosData.id);
                continue;
            }
            const threadTypeData = cloneDeep(axiosData.data);
            // remove first as the function will check if
            // this end state is in the retry queue and reject accordingly
            idToRemove.push(axiosData.id);
            setTimeout(() => {
                threadingStore.threadTasks({
                    data: threadTypeData.data,
                    endState: threadTypeData.endState,
                    type: 'api'
                });
            }, 100);
        }
        centralStore.removeFromQueue(idToRemove);
    }
    useObservable(userWatcher$.pipe(
    // filter if user is newly logged in
    filter(({ newValue, oldValue }) => {
        return ((
        // @ts-expect-error ignore
        [null, undefined, ''].indexOf(newValue) === -1) && (newValue !== oldValue));
    }), delay(500), tap(() => {
        retryCalls();
    })));
    const localStoreInitialized = computed(() => centralStore.localStoreInitialized);
    watch(user, (newValue, oldValue) => {
        if (!localStoreInitialized.value)
            return;
        userWatcher$.next({ newValue, oldValue });
    });
    const isOnline = computed(() => centralStore.online);
    const isBackendOnline = computed(() => centralStore.backendOnline);
    // retry if backend is down for every 2 second
    const { subject: backendOnlineWatcher$ } = rxInitSubjectCallback();
    useObservable(backendOnlineWatcher$.pipe(delay(2000), filter(() => isOnline.value), tap(() => {
        centralStore.toggleBackendOnline(true);
    }), tap(() => {
        retryCalls();
    })));
    watch(isOnline, (newValue, oldValue) => {
        if (!newValue)
            return;
        if (oldValue)
            return;
        // change to online as breaking of wifi can cause 408 as well
        centralStore.toggleBackendOnline(true);
        retryCalls();
    });
    watch(isBackendOnline, (newValue) => {
        if (!newValue) {
            backendOnlineWatcher$.next('');
        }
    });
};
export { retryWatchPlugin };
